"use client";

import { ChevronRight, User } from "lucide-react";
import type { GovernorListComponent, GovernorListItem } from "../../types";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { resolveLinkHref } from "../../utils/links";

/**
 * History grid for the APA governors. One card per governor — large portrait
 * on top, ordinal (`Nème Gouverneur`), full name, year range, full-card link
 * to the matching detail page.
 *
 * No filters / no search by design — this is a complete
 * historical roster. Cards sort by `ordre` descending (most recent governor
 * first); the interim Comité de Gestion (`ordre=0`) sinks to the bottom.
 *
 * Visual: portrait-led card (4:5 aspect, full card width) with a compact
 * white meta band below. Colours follow the site system: primary blue
 * `#00B862` headings, muted blue-grey `#6B7280` meta. Cards without a
 * portrait fall back to a generic user glyph centred in the slate frame
 * (e.g. the institutional "Comité de Gestion" interim).
 *
 * Description text is deliberately omitted — the home GovernorList behaves
 * as a photo gallery; the bio lives on each governor's detail page.
 */
export function GovernorListRenderer({ component }: { component: GovernorListComponent }) {
  const { title, items } = component.content;
  const sorted = [...(items ?? [])].sort((a, b) => parseOrdre(b.ordre) - parseOrdre(a.ordre));

  return (
    <section className="mt-8 md:mt-12">
      {title ? (
        <h3 className="mb-6 text-[24px] md:text-[28px] font-bold text-[#00B862]">{title}</h3>
      ) : null}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 sm:gap-6">
        {sorted.map((g, i) => (
          <GovernorCard key={`${g.href || g.title || "g"}-${i}`} item={g} />
        ))}
      </div>
    </section>
  );
}

function GovernorCard({ item }: { item: GovernorListItem }) {
  const locale = useLocale();
  // Resolve the authored href via the standard utility — same code path the
  // rest of the SDK uses (LinkTarget = string | {slug} | {external}). When
  // the CMS leaves `href` empty (vice-governors have no individual detail
  // page in the legacy data), the renderer trusts that and emits a plain
  // `<article>` instead of an anchor — no fake link, no "click goes
  // nowhere" affordance.
  const href = resolveLinkHref(item.href);
  const ordinal = formatOrdinal(item.ordre);
  const years = formatYearRange(item.dateDebut, item.dateFin);

  const portrait = (
    <div className="relative aspect-[4/5] w-full overflow-hidden bg-slate-100">
      {item.image ? (
        <RuntimeImage
          src={item.image}
          alt={item.title}
          className="absolute inset-0 h-full w-full object-cover object-top transition group-hover:scale-[1.02]"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-[#6B7280]">
          <User aria-hidden="true" className="h-16 w-16" />
        </div>
      )}
    </div>
  );

  const meta = (
    <div className="flex flex-1 flex-col p-3 md:p-4">
      {ordinal ? (
        <span className="text-[11px] md:text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
          {ordinal}
        </span>
      ) : null}
      <h4 className={`mt-1 line-clamp-2 text-[15px] md:text-[16px] font-semibold leading-snug text-[#00B862]${href ? " group-hover:underline" : ""}`}>
        {item.title}
      </h4>
      {years ? (
        <span className="mt-1 text-[12px] md:text-xs font-medium text-[#121212]/70">{years}</span>
      ) : null}
      {href ? (
        <div className="mt-auto flex items-center justify-end gap-1 pt-2 text-[11px] md:text-xs font-semibold text-[#00B862]">
          {t(locale, "readBiography")} <ChevronRight className="h-3.5 w-3.5" />
        </div>
      ) : null}
    </div>
  );

  const cardClass = "group flex h-full flex-col overflow-hidden rounded-md bg-white ring-1 ring-slate-200" + (href ? " transition hover:ring-[#00B862]/40" : "");

  if (href) {
    return (
      <RuntimeLink href={href} className={cardClass}>
        {portrait}
        {meta}
      </RuntimeLink>
    );
  }
  return (
    <article className={cardClass}>
      {portrait}
      {meta}
    </article>
  );
}

/** Numeric value of `ordre` for sorting. Returns -1 for missing/non-numeric so
 *  records like the interim "Comité de Gestion" (ordre=0) sit below numbered
 *  governors but above missing-ordre rows. */
function parseOrdre(ordre?: string | null): number {
  const n = Number((ordre ?? "").trim());
  return Number.isFinite(n) ? n : -1;
}

/** "Nème Gouverneur" / "1er Gouverneur" / "Intérim" for ordre=0.
 *  Empty / whitespace input returns "" — without the explicit guard,
 *  `Number("")` evaluates to 0 and every card with a missing `ordre`
 *  would falsely render as "Intérim". */
function formatOrdinal(ordre?: string | null): string {
  const raw = (ordre ?? "").trim();
  if (raw === "") return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "Intérim";
  if (n === 1) return "1er Gouverneur";
  return `${n}ème Gouverneur`;
}

/** "1997 – 2013" / "depuis 2025". Returns "" when no usable date. */
function formatYearRange(start?: string | null, end?: string | null): string {
  const sy = pickYear(start);
  const ey = pickYear(end);
  if (sy && ey) return `${sy} – ${ey}`;
  if (sy && !ey) return `depuis ${sy}`;
  if (!sy && ey) return `jusqu'en ${ey}`;
  return "";
}

function pickYear(d?: string | null): string {
  const m = (d ?? "").match(/^(\d{4})/);
  return m ? m[1] : "";
}
